import * as React from 'react';

import {ChatPreview} from 'shells/chat/chat-preview';
import {colors} from 'shells/colors/colors';
import {PageContentPreviewable} from 'shells/layout/page-content-previewable/page-content-previewable';
import {LoadingText} from 'shells/loading-placeholder/loading-text';
import {useManageSaveState} from 'shells/saved-setting-toast/use-manage-save-state';

import type {ChatIconEnum, ChatbotIconEnum, ChatVariantEnum} from 'nutshell-graphql-types';
import type {ChatSettingsLookAndFeelInput} from 'nutshell-graphql-types';

import {useGetSessionUser} from '../../../../core/use-get-session-user';
import {ChatEngagementProUpsellModal} from '../../../../limits/feature-gates/chat-engagement-pro-upsell-modal';

import {useUpdateChatSettings, useUpdateChatWidgetIcon} from '../../graphql/hooks';
import {useGetAppearanceSettings} from '../../graphql/hooks/use-get-appearance-settings';
import {useGetPrimaryContextDocument} from '../../graphql/hooks/use-get-primary-context-document';

import {SAVE_DELAY, CHAT_VARIANT} from '../consts';
import type {SaveStateKey} from '../consts';
import {getChatbotIcon, getChatWidgetIcon} from '../helpers';
import {SettingsContentWrapper} from '../settings-content-wrapper';

import {BrandingSection} from './branding-section';

export function BrandingOptions() {
    const [chatVariant, setChatVariant] = React.useState<ChatVariantEnum>(CHAT_VARIANT.CHAT);
    const [color, setColor] = React.useState<string>('#000000');
    const [secondaryColor, setSecondaryColor] = React.useState<string>('#FFFFFF');
    const [name, setName] = React.useState<string>('');
    const [chatbotName, setChatbotName] = React.useState<string>('');
    const [chatbotIconEnum, setChatbotIconEnum] = React.useState<ChatbotIconEnum>('CHATBOT');
    const [conversationStarter, setConversationStarter] = React.useState<string>('');
    const [showPoweredBy, setShowPoweredBy] = React.useState<boolean>(false);
    const [isShowPoweredByUpsellModalOpen, setIsShowPoweredByUpsellModalOpen] =
        React.useState(false);

    // iconType and iconUrl are used to determine the type of icon to display and are fetched from the query based on what we have in the db
    const [iconType, setIconType] = React.useState<ChatIconEnum>('CHAT_BUBBLE');
    // if iconType is set to 'CUSTOM' then we display the custom icon from the url, otherwise we display the default icon which is one of three unicode strings representing emojis
    const [iconUrl, setIconUrl] = React.useState<string | null | undefined>('');

    const {user} = useGetSessionUser();
    const {updateChatSettings} = useUpdateChatSettings();
    const {updateChatWidgetIcon} = useUpdateChatWidgetIcon();
    const {chatSettings, isLoading, refetch: refetchSettings} = useGetAppearanceSettings();
    const {primaryContextDocument, isLoading: isLoadingSuggestions} =
        useGetPrimaryContextDocument();

    React.useEffect(() => {
        if (chatSettings) {
            const {lookAndFeel} = chatSettings;
            setChatVariant(lookAndFeel.chatVariant);
            setColor(lookAndFeel.color);
            setSecondaryColor(lookAndFeel.secondaryColor);
            setName(lookAndFeel.nameDisplay);
            setChatbotName(lookAndFeel.chatbotName);
            setChatbotIconEnum(lookAndFeel.chatbotIconEnum);
            setConversationStarter(lookAndFeel.conversationStarter);
            setIconType(lookAndFeel.icon.type);
            setIconUrl(lookAndFeel.icon.url);
            setShowPoweredBy(lookAndFeel.showPoweredBy);
        }
    }, [chatSettings]);

    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const {toastState} = useManageSaveState(isSaving, SAVE_DELAY);
    const [savingToastKey, setSavingToastKey] = React.useState<SaveStateKey | null | undefined>(
        null
    );

    const handleSave = (updates: ChatSettingsLookAndFeelInput, toastKey: SaveStateKey) => {
        if (!chatSettings) return Promise.resolve();

        setIsSaving(true);
        setSavingToastKey(toastKey);

        return updateChatSettings({
            chatSettings: {
                lookAndFeel: updates,
            },
        }).then(() => {
            refetchSettings();
            setIsSaving(false);
            setTimeout(() => {
                setSavingToastKey(null);
            }, 1000);
        });
    };

    const handleIconSave = (newIconType: ChatIconEnum, newIconUrl?: string | null) => {
        setIsSaving(true);
        setSavingToastKey('icon');

        updateChatWidgetIcon({
            type: newIconType,
            imageUrl: newIconUrl,
        }).then(() => {
            refetchSettings();
            setIsSaving(false);
            setTimeout(() => {
                setSavingToastKey(null);
            }, 1000);
        });
    };

    const actionBarEnabled = chatVariant === CHAT_VARIANT.ACTIONS;
    const suggestions = (primaryContextDocument && primaryContextDocument.aiSuggestions) || [];
    const hasEngagementPro = user && user.permissions.canUseSms;

    const getPreview = () => {
        if (!chatSettings) {
            return (
                <div className='flex justify-center align-center full-width full-height'>
                    <LoadingText height={500} width={305} color={colors.plumLt} />
                </div>
            );
        }

        return (
            <ChatPreview
                headerTitle={name}
                backgroundColor={color}
                status='appearance'
                icon={getChatWidgetIcon(iconType, iconUrl)}
                conversationStarter={conversationStarter}
                showPoweredBy={showPoweredBy}
                showDisclaimer={chatSettings.lookAndFeel.disclaimerSettings.isEnabled}
                disclaimerText={chatSettings.lookAndFeel.disclaimerSettings.message}
                secondaryColor={secondaryColor}
                chatbotName={chatbotName}
                chatbotIcon={getChatbotIcon(chatbotIconEnum)}
                actionBarEnabled={actionBarEnabled}
                actionBarShownActions={chatSettings.lookAndFeel.actionBarShownActions}
                actions={chatSettings.actions}
                showNudge={chatSettings.lookAndFeel.nudgeSettings.isEnabled}
                suggestions={suggestions}
                isLoadingSuggestions={isLoadingSuggestions}
            />
        );
    };

    return (
        <SettingsContentWrapper>
            <PageContentPreviewable
                getPreview={getPreview}
                leftContent={
                    <div className='justify-sb flex flex-dir-col full-height mb-32 gap-32 pr-8'>
                        <BrandingSection
                            isLoading={isLoading}
                            toastState={toastState}
                            savingToastKey={savingToastKey}
                            color={color}
                            secondaryColor={secondaryColor}
                            iconType={iconType}
                            iconUrl={iconUrl}
                            name={name}
                            chatbotName={chatbotName}
                            chatbotIconEnum={chatbotIconEnum}
                            showPoweredBy={showPoweredBy}
                            hasEngagementPro={hasEngagementPro}
                            handleSave={handleSave}
                            setColor={setColor}
                            setSecondaryColor={setSecondaryColor}
                            setIconType={setIconType}
                            setIconUrl={setIconUrl}
                            handleIconSave={handleIconSave}
                            setChatbotIconEnum={setChatbotIconEnum}
                            setShowPoweredBy={setShowPoweredBy}
                            setIsShowPoweredByUpsellModalOpen={setIsShowPoweredByUpsellModalOpen}
                        />
                    </div>
                }
            />
            <ChatEngagementProUpsellModal
                isOpen={isShowPoweredByUpsellModalOpen}
                onClose={() => setIsShowPoweredByUpsellModalOpen(false)}
            />
        </SettingsContentWrapper>
    );
}
